/**
 * px → rem dönüşümü — kaynak-referans `theme/layout/RemFromPx.ts` paritesi (1:1 port).
 *
 * 16px kök taban üzerinden pikseli rem'e çevirir; sondaki sıfır gürültüsünü
 * önlemek için 5 ondalık basamağa yuvarlar. Saf matematiktir; hiçbir dış
 * bağımlılığı yoktur.
 */

export const REM_BASE_PX = 16;

/** Piksel değerini `${n}rem` string'ine çevirir (5 ondalık hassasiyet). */
export function remFromPx(px: number): `${number}rem` {
  const rounded = Math.round((px / REM_BASE_PX) * 1e5) / 1e5;
  return `${rounded}rem`;
}

/* ── SES 1-1 YENİDEN EŞİTLEME (17 Eyl 2026): kaynak-referans rem-ölçek izleme ──
 * Belge kök font-size (rem tabanı) 16px'ten sapınca (tarayıcı yakınlaştırma /
 * erişilebilirlik font ölçeği) piksel-rem dönüşümlerinin gerçek ölçeği. Sonuç
 * belge başına önbelleklenir; `clearRemScaleCache` (tümü) veya
 * `startRemScaleTracking` (resize/DPR değişiminde otomatik) ile geçersiz kılınır. */

interface CachedRemScale {
  scale: number;
  generation: number;
}

const cachedRemScales = new WeakMap<Document, CachedRemScale>();
const remScaleDisposers = new WeakMap<Document, () => void>();
let remScaleGeneration = 0;

function readRemScaleFromDocument(ownerDocument: Document): number {
  const ownerWindow = ownerDocument.defaultView;
  if (ownerWindow == null) {
    return 1;
  }
  const rootFontSize = Number.parseFloat(ownerWindow.getComputedStyle(ownerDocument.documentElement).fontSize);
  if (!Number.isFinite(rootFontSize) || rootFontSize <= 0) {
    return 1;
  }
  return rootFontSize / REM_BASE_PX;
}

/** Belgenin rem ölçeği (kök font-size / 16). Belge yoksa 1. */
export function getRemScaleForDocument(ownerDocument: Document | null): number {
  if (ownerDocument == null) {
    return 1;
  }
  const cached = cachedRemScales.get(ownerDocument);
  if (cached != null && cached.generation === remScaleGeneration) {
    return cached.scale;
  }
  const scale = readRemScaleFromDocument(ownerDocument);
  cachedRemScales.set(ownerDocument, { scale, generation: remScaleGeneration });
  return scale;
}

/** Ölçek önbelleğini boşaltır (belge verilmezse tüm belgeler için). */
export function clearRemScaleCache(ownerDocument?: Document | null): void {
  if (ownerDocument == null) {
    remScaleGeneration += 1;
    return;
  }
  cachedRemScales.delete(ownerDocument);
}

/** Pencere yeniden boyutlanma / DPR değişiminde ölçek önbelleğini otomatik geçersiz kılar. */
export function startRemScaleTracking(ownerDocument: Document): () => void {
  remScaleDisposers.get(ownerDocument)?.();
  const ownerWindow = ownerDocument.defaultView;
  if (ownerWindow == null) {
    return () => {};
  }
  return trackRemScaleForWindow(ownerDocument, ownerWindow);
}

function trackRemScaleForWindow(ownerDocument: Document, ownerWindow: Window): () => void {
  let resolutionQuery: MediaQueryList | null = null;
  let trackedDevicePixelRatio = ownerWindow.devicePixelRatio;
  let trackedInnerWidth = ownerWindow.innerWidth;

  function invalidate(): void {
    cachedRemScales.delete(ownerDocument);
  }

  function stopWatchingResolution(): void {
    resolutionQuery?.removeEventListener("change", invalidateForRootFontSizeInputs);
    resolutionQuery = null;
  }

  function watchResolution(): void {
    if (typeof ownerWindow.matchMedia !== "function") {
      return;
    }
    const nextQuery = ownerWindow.matchMedia(`(resolution: ${ownerWindow.devicePixelRatio}dppx)`);
    if (!nextQuery.matches) {
      stopWatchingResolution();
      return;
    }
    stopWatchingResolution();
    resolutionQuery = nextQuery;
    resolutionQuery.addEventListener("change", invalidateForRootFontSizeInputs);
  }

  function invalidateForRootFontSizeInputs(): void {
    trackedDevicePixelRatio = ownerWindow.devicePixelRatio;
    trackedInnerWidth = ownerWindow.innerWidth;
    invalidate();
    watchResolution();
  }

  function handleResize(): void {
    if (ownerWindow.devicePixelRatio === trackedDevicePixelRatio && ownerWindow.innerWidth === trackedInnerWidth) {
      return;
    }
    invalidateForRootFontSizeInputs();
  }

  ownerWindow.addEventListener("resize", handleResize);
  watchResolution();
  invalidate();

  const dispose = (): void => {
    if (remScaleDisposers.get(ownerDocument) !== dispose) {
      return;
    }
    ownerWindow.removeEventListener("resize", handleResize);
    stopWatchingResolution();
    remScaleDisposers.delete(ownerDocument);
    cachedRemScales.delete(ownerDocument);
  };
  remScaleDisposers.set(ownerDocument, dispose);
  return dispose;
}
